import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/feature/SeoHead';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { articleImages } from '@/mocks/articleImages';

const ogImage = "https://readdy.ai/api/search-image?query=Abstract%20color%20palette%20with%20warm%20coral%20amber%20and%20cream%20tones%20flowing%20together%20in%20a%20minimalist%20artistic%20composition%2C%20soft%20gradients%2C%20elegant%20and%20creative%20atmosphere%2C%20modern%20design%20aesthetic&width=1200&height=630&seq=home-og-2026&orientation=landscape";

const heroJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '就去色色',
  url: 'https://jiuqu.sese',
  description: '探索色彩美学与视觉创作的灵感平台。专注色彩搭配、视觉美学与创意设计灵感，为设计师、摄影师和艺术爱好者提供最全面的色彩资源。',
  potentialAction: { '@type': 'SearchAction', target: 'https://jiuqu.sese/search?q={search_term_string}', 'query-input': 'required name=search_term_string' },
};

const orgJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '就去色色',
  url: 'https://jiuqu.sese',
  description: '专注色彩搭配、视觉美学与创意设计灵感的内容平台。',
  sameAs: ['https://weibo.com/jiuqusese', 'https://www.xiaohongshu.com/jiuqusese'],
};

const featuredArticles = articles.filter((a) => a.featured).slice(0, 6);
const latestArticles = articles.filter((a) => !a.featured).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 6);

function ArticleCard({ article }: { article: typeof articles[0] }) {
  const imgSrc = articleImages[article.slug] || '';
  return (
    <Link to={`/article/${article.slug}`} className="group block bg-background-50 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-300/50 transition-all duration-300">
      <div className="relative w-full h-48 overflow-hidden">
        {imgSrc && <img src={imgSrc} alt={article.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />}
        <div className="absolute top-3 left-3">
          <span className="bg-primary-500/90 text-background-50 text-xs px-2.5 py-1 rounded-full whitespace-nowrap">{article.categoryName}</span>
        </div>
      </div>
      <div className="p-5">
        <h3 className="text-base font-heading text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">{article.title}</h3>
        <p className="mt-2 text-sm text-foreground-500 line-clamp-2 leading-relaxed">{article.excerpt}</p>
        <div className="mt-4 flex items-center justify-between text-xs text-foreground-400">
          <span>{article.date}</span>
          <span>{article.readTime} 分钟阅读</span>
        </div>
      </div>
    </Link>
  );
}

export default function HomePage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <>
      <SeoHead title="探索色彩美学与视觉创作灵感" description="就去色色，专注色彩搭配、视觉美学与创意设计灵感。无论你是设计师、摄影师还是艺术爱好者，就去色色为你提供最全面的色彩理论、配色方案与视觉创作资源，激发无限创意灵感。" keywords="就去色色,色彩搭配灵感,视觉美学设计,创意配色方案,色彩理论学习,设计师配色工具,艺术色彩探索" canonicalUrl="/" ogImage={ogImage} jsonLd={{ ...heroJsonLd, ...orgJsonLd }} />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <header className="relative w-full h-[520px] md:h-[650px] flex items-center justify-center overflow-hidden">
          <img src="https://readdy.ai/api/search-image?query=Abstract%20flowing%20gradients%20of%20warm%20coral%20peach%20amber%20and%20cream%20tones%20with%20gentle%20curves%20and%20organic%20shapes%2C%20soft%20dreamy%20atmosphere%2C%20minimalist%20artistic%20composition%2C%20ethereal%20and%20elegant%20color%20transitions%2C%20modern%20aesthetic%20with%20subtle%20texture&width=1800&height=800&seq=hero-bg-main-2026&orientation=landscape" alt="色彩美学" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/45"></div>
          <div className="relative z-10 w-full max-w-4xl mx-auto px-4 text-center">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading text-background-50 leading-tight tracking-wide">探索色彩的无尽魅力</h1>
            <p className="mt-5 md:mt-6 text-base md:text-lg text-background-200 max-w-2xl mx-auto leading-relaxed">就去色色，专注色彩搭配、视觉美学与创意设计灵感。激发你的无限创意，让每一抹色彩都充满故事。</p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link to="/category/color-theory" className="bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3 rounded-full text-sm font-medium transition-all whitespace-nowrap">开始探索</Link>
              <Link to="/about" className="bg-background-50/20 hover:bg-background-50/30 text-background-50 border border-background-50/30 px-6 py-3 rounded-full text-sm font-medium transition-all whitespace-nowrap backdrop-blur-sm">关于我们</Link>
            </div>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
              {categories.map((cat) => (
                <Link key={cat.id} to={`/category/${cat.slug}`} className="bg-background-50/15 hover:bg-background-50/25 text-background-100 backdrop-blur-sm px-4 py-2 rounded-full text-xs font-medium transition-all whitespace-nowrap border border-background-50/20">{cat.name}</Link>
              ))}
            </div>
          </div>
        </header>

        <section className="bg-background-100 py-8 md:py-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              {[{ value: '200+', label: '深度文章' },{ value: '50+', label: '配色方案' },{ value: '30K+', label: '月度读者' },{ value: '5', label: '内容分类' }].map((stat) => (
                <div key={stat.label}><div className="text-2xl md:text-3xl font-heading text-primary-500">{stat.value}</div><div className="mt-1 text-sm text-foreground-500">{stat.label}</div></div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-900">探索内容分类</h2>
              <p className="mt-3 text-sm md:text-base text-foreground-500 max-w-xl mx-auto">从色彩理论到创意工具，五大内容板块全面覆盖你的学习需求</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
              {categories.map((cat, idx) => {
                const badgeStyles = [
                  'bg-primary-100 text-primary-700',
                  'bg-accent-100 text-accent-700',
                  'bg-secondary-100 text-secondary-700',
                  'bg-accent-50 text-accent-600',
                  'bg-secondary-50 text-secondary-600',
                ];
                const iconStyles = [
                  'bg-primary-100 text-primary-600 group-hover:bg-primary-200',
                  'bg-accent-100 text-accent-600 group-hover:bg-accent-200',
                  'bg-secondary-100 text-secondary-600 group-hover:bg-secondary-200',
                  'bg-accent-50 text-accent-500 group-hover:bg-accent-100',
                  'bg-secondary-50 text-secondary-500 group-hover:bg-secondary-100',
                ];
                return (
                  <Link key={cat.id} to={`/category/${cat.slug}`} className="group bg-background-50 rounded-xl p-5 border border-background-200/60 hover:border-primary-300/50 transition-all duration-300 flex flex-col items-center text-center">
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-4 transition-colors ${iconStyles[idx]}`}><i className={`${cat.icon} text-2xl`}></i></div>
                    <h3 className="text-base font-heading text-foreground-900 group-hover:text-primary-500 transition-colors">{cat.name}</h3>
                    <p className="mt-2 text-xs text-foreground-500 leading-relaxed line-clamp-2">{cat.description}</p>
                    <span className={`mt-3 text-xs px-2.5 py-0.5 rounded-full ${badgeStyles[idx]} whitespace-nowrap`}>{cat.articleCount} 篇</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-end justify-between mb-10">
              <div><h2 className="text-2xl md:text-3xl font-heading text-foreground-900">精选文章</h2><p className="mt-2 text-sm text-foreground-500">编辑精选的优质内容，带你深入色彩世界</p></div>
              <Link to="/category/color-theory" className="hidden md:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap">查看全部 <i className="ri-arrow-right-line"></i></Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredArticles.map((article) => <ArticleCard key={article.id} article={article} />)}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-end justify-between mb-10">
              <div><h2 className="text-2xl md:text-3xl font-heading text-foreground-900">最新文章</h2><p className="mt-2 text-sm text-foreground-500">持续更新的色彩美学与设计灵感内容</p></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {latestArticles.map((article) => <ArticleCard key={article.id} article={article} />)}
            </div>
          </div>
        </section>

        <section className="relative py-20 md:py-28 overflow-hidden">
          <img src="https://readdy.ai/api/search-image?query=Soft%20flowing%20gradients%20of%20warm%20coral%20and%20golden%20amber%20tones%20with%20gentle%20organic%20waves%2C%20dreamy%20ethereal%20atmosphere%2C%20minimalist%20abstract%20composition%2C%20warm%20and%20inviting%20color%20palette%2C%20soft%20focus%20aesthetic%20with%20smooth%20transitions%2C%20elegant%20modern%20design&width=1800&height=600&seq=cta-bg-2026&orientation=landscape" alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="relative z-10 max-w-3xl mx-auto px-4 text-center">
            <h2 className="text-2xl md:text-4xl font-heading text-background-50">开始你的色彩探索之旅</h2>
            <p className="mt-4 text-sm md:text-base text-background-200 max-w-xl mx-auto leading-relaxed">加入数千名设计师和创意人，一起探索色彩的无限可能。每周更新深度色彩内容，激发你的创作灵感。</p>
            <div className="mt-8">
              <Link to="/category/design-inspiration" className="inline-flex items-center gap-2 bg-background-50 hover:bg-background-100 text-foreground-900 px-8 py-3.5 rounded-full text-sm font-medium transition-all whitespace-nowrap">浏览设计灵感 <i className="ri-arrow-right-line"></i></Link>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}